import React, { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { claimJoinInvitation, validateJoinInvitation } from "../../api/api";
import { adoptAccount } from "../../lib/sessionActions";
import { BrandLogo } from "../nav_panel/BrandLogo";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import UsernameField from "../../widgets/UsernameField";

const underlineClass =
  "h-10 w-full border-0 border-b border-border bg-transparent px-0 text-base text-text-primary shadow-none outline-none placeholder:text-text-muted focus:border-accent";

/**
 * Small icon control that copies a value and briefly shows a check.
 * @param {Object} props
 * @param {string} props.value
 * @param {string} props.label
 * @param {string} props.copied
 * @param {(value: string, label: string) => void} props.onCopy
 * @returns {JSX.Element}
 */
function CopyIconButton({ value, label, copied, onCopy }) {
  const isCopied = copied === label;
  return (
    <button
      type="button"
      aria-label={isCopied ? `${label} copied` : `Copy ${label}`}
      onClick={() => onCopy(value, label)}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-secondary hover:text-text-primary"
    >
      {isCopied ? (
        <Check className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Copy className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * Short join page for an invited individual. The email stays on the server.
 * @param {Object} props
 * @param {(user: object) => void} props.stageSession
 * @returns {JSX.Element}
 */
export default function JoinPage({ stageSession }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [preApproved, setPreApproved] = useState(false);
  const [username, setUsername] = useState("");
  const [usernameValid, setUsernameValid] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [account, setAccount] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!token) {
      setPageError("This invitation link is missing a token.");
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const result = await validateJoinInvitation(token);
      if (cancelled) return;
      if (result.error || !result.data) {
        setPageError(result.error || "This invitation is invalid or expired.");
        setLoading(false);
        return;
      }
      const preview = result.data;
      setFirstName(preview.first_name || "");
      setLastName(preview.last_name || "");
      setPreApproved(Boolean(preview.pre_approved));
      setUsername(preview.suggested_username || "");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  /**
   * @param {string} value
   * @param {string} label
   * @returns {Promise<void>}
   */
  async function copyValue(value, label) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
    } catch (err) {
      setCopied("");
    }
  }

  /**
   * @param {React.FormEvent} event
   * @returns {Promise<void>}
   */
  async function onSubmit(event) {
    event.preventDefault();
    if (!usernameValid || !termsAgreed || !firstName.trim() || !lastName.trim()) return;
    setSubmitting(true);
    setSubmitError("");
    const result = await claimJoinInvitation({
      token,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      username: username.trim(),
      terms_accepted: true,
    });
    setSubmitting(false);
    if (result.error || !result.data) {
      setSubmitError(result.error || "Could not create the account");
      return;
    }
    setAccount({
      user: result.data,
      token: result.token || "",
      password: result.temporary_password || "",
      username: result.data.username || username.trim(),
      firstName: firstName.trim(),
      preApproved,
    });
  }

  /**
   * @returns {void}
   */
  async function goToProfile() {
    if (!account?.user?.id) return;
    const user = { ...account.user, type: account.user.type || "user" };
    if (account.token) {
      const adopted = await adoptAccount(user, account.token);
      if (adopted.error) {
        setSubmitError(adopted.error);
        return;
      }
    }
    stageSession(user);
    navigate(`/profile/${user.id}`);
  }

  const greeting = account?.firstName ? `Welcome, ${account.firstName}` : "Welcome";

  return (
    <main className="min-h-full bg-primary px-6 py-16 text-text-primary">
      <div className="mx-auto flex w-full max-w-md flex-col">
        <div className="mb-16 flex justify-center">
          <BrandLogo />
        </div>

        {loading ? <p className="text-text-secondary">Checking your invitation…</p> : null}

        {!loading && pageError ? (
          <div className="space-y-4">
            <p className="text-base text-text-primary" role="alert">
              {pageError}
            </p>
            <Link to="/login" className="text-sm text-accent">
              Log in
            </Link>
          </div>
        ) : null}

        {!loading && !pageError && !account ? (
          <form className="space-y-8" onSubmit={onSubmit}>
            <div>
              {preApproved ? (
                <div className="rounded-lg border border-accent bg-accent/10 px-4 py-3">
                  <h1 className="text-sm font-semibold text-accent">You&apos;re pre-approved</h1>
                  <p className="mt-1 text-sm text-text-primary">
                    Welcome. Your place on Assembly is ready as soon as you join.
                  </p>
                </div>
              ) : (
                <h1 className="text-2xl font-semibold text-text-primary">
                  You&apos;ve been invited to join Assembly Platform.
                </h1>
              )}
              <p className="mt-3 text-sm text-text-secondary">
                Confirm your name, then choose the username you want to be known by.
              </p>
            </div>
            <div>
              <label htmlFor="join-first-name" className="mb-2 block text-sm text-text-secondary">
                First name
              </label>
              <input
                id="join-first-name"
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                className={underlineClass}
              />
            </div>
            <div>
              <label htmlFor="join-last-name" className="mb-2 block text-sm text-text-secondary">
                Last name
              </label>
              <input
                id="join-last-name"
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                className={underlineClass}
              />
            </div>
            <UsernameField
              id="join-username"
              label="Username"
              value={username}
              onChange={setUsername}
              onValidityChange={setUsernameValid}
              inputClassName="!h-10 !rounded-none !border-x-0 !border-t-0 !border-b !border-border !bg-transparent !px-0 !shadow-none focus-visible:!ring-0 focus-visible:!ring-offset-0"
            />
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="join-terms"
                  checked={termsAgreed}
                  onCheckedChange={setTermsAgreed}
                  className="mt-0.5"
                />
                <label htmlFor="join-terms" className="text-sm text-text-secondary">
                  I agree to the Assembly terms.
                </label>
              </div>
              <details className="text-sm text-text-secondary">
                <summary className="cursor-pointer text-text-primary">Read the terms</summary>
                <div className="mt-3 space-y-2">
                  <p>Assembly exists to unite the body of Christ.</p>
                  <p>
                    This is a platform for connection and collaboration. Accounts should represent
                    real ministry leaders and should not be used to impersonate someone else.
                  </p>
                  <p>
                    Independent ministry leaders may be reviewed before the rest of the platform
                    opens. You can still finish your profile while that review is underway.
                  </p>
                </div>
              </details>
            </div>
            {submitError ? (
              <p className="text-sm text-error" role="alert">
                {submitError}
              </p>
            ) : null}
            <Button
              type="submit"
              size="sm"
              disabled={
                submitting || !firstName.trim() || !lastName.trim() || !usernameValid || !termsAgreed
              }
            >
              {submitting ? "Joining…" : "Join"}
            </Button>
          </form>
        ) : null}

        {account ? (
          <div className="space-y-8">
            <div>
              <p className="text-sm font-medium text-accent">You&apos;re in</p>
              <h1 className="mt-2 text-3xl font-semibold text-text-primary">{greeting}</h1>
              <p className="mt-3 text-sm text-text-secondary">
                {account.preApproved
                  ? "Your account is approved. These are your sign-in details, and a copy is on its way to your email."
                  : "These are your sign-in details, and a copy is on its way to your email. You can change them later in settings."}
              </p>
            </div>
            <div>
              <p className="mb-1 text-sm text-text-secondary">Username</p>
              <div className="flex items-center justify-between gap-3 border-b border-border py-2">
                <p className="truncate text-text-primary">{account.username}</p>
                <CopyIconButton
                  value={account.username}
                  label="username"
                  copied={copied}
                  onCopy={copyValue}
                />
              </div>
            </div>
            <div>
              <p className="mb-1 text-sm text-text-secondary">Temporary password</p>
              <div className="flex items-center justify-between gap-3 border-b border-border py-2">
                <p className="truncate text-text-primary">{account.password}</p>
                <CopyIconButton
                  value={account.password}
                  label="password"
                  copied={copied}
                  onCopy={copyValue}
                />
              </div>
            </div>
            {submitError ? (
              <p className="text-sm text-error" role="alert">
                {submitError}
              </p>
            ) : null}
            <Button type="button" size="sm" onClick={goToProfile}>
              Go to your profile
            </Button>
          </div>
        ) : null}
      </div>
    </main>
  );
}
